import React, { Fragment, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Btn, H4 } from '../../AbstractElements'
import { Row, Col, CardHeader, CardBody, CardFooter, Form, FormGroup, Label, Input } from 'reactstrap'
import { Breadcrumbs } from '../../AbstractElements'
import { getIdData, updateData } from '../../lib/profiles'
import { ClassForLessonSelect } from '../Common/FormSelects'

const StudentUpdate = () => {
	const navigate = useNavigate()

	const { id } = useParams()

	// Form state
	const [formData, setFormData] = useState({
		name: '',
		surname: '',
		phone: '',
		username: '',
		email: '',
		class: null,
		studentschool: null,
		graduateyear: '',
		gpa: '',
		satpsat: '',
		statetest: '',
		major: '',
		career: '',
		fiveAdjectives: null,
		hsSchoolStatistics: null,
		topThreeColleges: null,
		lor: null,
		activities: null,
		collegeEssay: null,
		instructorNote: null,
		studentNote: null,
		meetlink: '',
		folderlink: ''
	})

	const [loading, setLoading] = useState(true)

	// Handle input changes
	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value
		}))
	}

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault()

		try {
			await updateData({
				id: id,
				...formData
			})
			navigate(-1)
		} catch (error) {
			console.error('Error updating student:', error)
		}
	}

	// Fetch student data
	useEffect(() => {
		const fetchStudentData = async () => {
			try {
				setLoading(true)
				const studentData = await getIdData(id)

				if (studentData && studentData.length > 0) {
					const student = studentData[0]
					setFormData({
						name: student.name,
						surname: student.surname,
						phone: student.phone,
						username: student.username,
						email: student.email,
						class: student.class,
						studentschool: student.studentschool,
						graduateyear: student.graduateyear,
						gpa: student.gpa,
						satpsat: student.satpsat,
						statetest: student.statetest,
						major: student.major,
						career: student.career,
						fiveAdjectives: student.fiveAdjectives,
						hsSchoolStatistics: student.hsSchoolStatistics,
						topThreeColleges: student.topThreeColleges,
						lor: student.lor,
						activities: student.activities,
						collegeEssay: student.collegeEssay,
						instructorNote: student.instructorNote,
						studentNote: student.studentNote,
						meetlink: student.meetlink,
						folderlink: student.folderlink
					})
				}
			} catch (error) {
				console.error('Error fetching student data:', error)
			} finally {
				setLoading(false)
			}
		}

		if (id) {
			fetchStudentData()
		}
	}, [id])

	if (loading) {
		return (
			<>
				<Breadcrumbs parent="Class Management" title="Update Student" mainTitle="Update Student" />
				<div className="text-center p-5">
					<div className="spinner-border text-primary" role="status">
						<span className="sr-only">Loading...</span>
					</div>
				</div>
			</>
		)
	}

	return (
		<Fragment>
			<Breadcrumbs parent="Class Management" title="Update Student" mainTitle="Update Student" />
			<Form className="card" onSubmit={handleSubmit}>
				<CardHeader>
					<H4 attrH4={{ className: 'card-title mb-0' }}>Update Student Profile</H4>
					<div className="card-options">
						<a className="card-options-collapse" href="#javascript">
							<i className="fe fe-chevron-up"></i>
						</a>
						<a className="card-options-remove" href="#javascript">
							<i className="fe fe-x"></i>
						</a>
					</div>
				</CardHeader>
				<CardBody>
					<Row>
						{/* Basic Information */}
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">First Name</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter First Name"
									value={formData.name}
									onChange={(e) => handleInputChange('name', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Last Name</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter Last Name"
									value={formData.surname}
									onChange={(e) => handleInputChange('surname', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Nick Name</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter Nick Name"
									value={formData.username}
									onChange={(e) => handleInputChange('username', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Email</Label>
								<Input
									className="form-control"
									type="email"
									placeholder="Enter Email"
									value={formData.email}
									onChange={(e) => handleInputChange('email', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Phone</Label>
								<Input
									className="form-control"
									type="tel"
									placeholder="123-456-7890"
									value={formData.phone}
									onChange={(e) => handleInputChange('phone', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Grade Level</Label>
								<ClassForLessonSelect value={formData.class} setValue={(value) => handleInputChange('class', value)} name="ClassForLessonSelect" />
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">School</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter School Name"
									value={formData.studentschool}
									onChange={(e) => handleInputChange('studentschool', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col sm="6" md="6">
							<FormGroup>
								<Label className="form-label">Graduation Year</Label>
								<Input
									className="form-control"
									type="number"
									placeholder="Enter Graduation Year"
									value={formData.graduateyear}
									onChange={(e) => handleInputChange('graduateyear', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* Academic Information */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">GPA (Weighted and unweighted if known)</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter GPA"
									value={formData.gpa}
									onChange={(e) => handleInputChange('gpa', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">SAT/PSAT Scores and Testing Date</Label>
								<Input
									type="textarea"
									className="form-control"
									rows="3"
									placeholder="Enter SAT/PSAT scores and testing date"
									value={formData.satpsat}
									onChange={(e) => handleInputChange('satpsat', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Practice SAT Scores and Testing Date</Label>
								<Input
									type="textarea"
									className="form-control"
									rows="3"
									placeholder="Enter Practice SAT scores and testing date"
									value={formData.statetest}
									onChange={(e) => handleInputChange('statetest', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* Personal Information */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Five Adjectives (Character traits that describe you)</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter five character trait adjectives"
									value={formData.fiveAdjectives}
									onChange={(e) => handleInputChange('fiveAdjectives', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">About My High School</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="About my high school"
									value={formData.hsSchoolStatistics}
									onChange={(e) => handleInputChange('hsSchoolStatistics', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* College Information */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Major (What do you want to study in college?)</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter desired major"
									value={formData.major}
									onChange={(e) => handleInputChange('major', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Career (What do you want to do after college?)</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter desired career"
									value={formData.career}
									onChange={(e) => handleInputChange('career', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Top Three College Choices</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Enter top three college choices"
									value={formData.topThreeColleges}
									onChange={(e) => handleInputChange('topThreeColleges', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* Additional Information */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Letter of Recommendation</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="Did you get one? If so, which teacher wrote it?"
									value={formData.lor}
									onChange={(e) => handleInputChange('lor', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Activities (Outside of high school hours)</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="List all activities outside of your high school hours"
									value={formData.activities}
									onChange={(e) => handleInputChange('activities', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">College Essay Progress</Label>
								<Input
									className="form-control"
									type="text"
									placeholder="What progress have you made on your essay so far?"
									value={formData.collegeEssay}
									onChange={(e) => handleInputChange('collegeEssay', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* Notes */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Instructor's Comment</Label>
								<Input
									type="textarea"
									className="form-control"
									rows="3"
									placeholder="Enter instructor's comment"
									value={formData.instructorNote}
									onChange={(e) => handleInputChange('instructorNote', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Student's Comment</Label>
								<Input
									type="textarea"
									className="form-control"
									rows="3"
									placeholder="Enter student's comment"
									value={formData.studentNote}
									onChange={(e) => handleInputChange('studentNote', e.target.value)}
								/>
							</FormGroup>
						</Col>

						{/* Links */}
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Zoom Link</Label>
								<Input
									className="form-control"
									type="url"
									placeholder="Enter Zoom Link"
									value={formData.meetlink}
									onChange={(e) => handleInputChange('meetlink', e.target.value)}
								/>
							</FormGroup>
						</Col>
						<Col md="12">
							<FormGroup>
								<Label className="form-label">Folder Link</Label>
								<Input
									className="form-control"
									type="url"
									placeholder="Enter Folder Link"
									value={formData.folderlink}
									onChange={(e) => handleInputChange('folderlink', e.target.value)}
								/>
							</FormGroup>
						</Col>
					</Row>
				</CardBody>
				<CardFooter className="text-end">
					<Btn attrBtn={{ color: 'primary', type: 'submit', className: 'me-2' }}>Update Student</Btn>
					<Btn attrBtn={{ color: 'secondary', className: 'me-2', onClick: () => navigate(-1) }}>Cancel</Btn>
				</CardFooter>
			</Form>
		</Fragment>
	)
}

export { StudentUpdate }
